import { useState } from 'react'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useGetReturnHub, useUpdateReturnHub, useCreateReturnHub } from '@dhl/mdp-api'
import { Box, Card } from '@dhl-ui/components'

import { links } from '~/AppRoutes'
import { useNotificationMessage } from '~/common'
import { useAddressService } from '~/common/components/addresses'
import { getErrorMessage } from '~/common/util/axiosUtil'
import { CancelDialog, ReturnHubForm } from '~/modules/returnhubsccc/components'
import {
  convertToNewReturnHub,
  convertToReturnHub,
  createReturnHubsValidationSchema,
  initialReturnHub,
  type ReturnHub,
} from '~/modules/returnhubsccc/model'
import { BackButton } from '~/temp-ds-component/BackButton'

export const ReturnHubFormPage = () => {
  const { id } = useParams()
  const addNew = !id
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { showError, showSuccess } = useNotificationMessage()
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false)

  const { mutateAsync: postReturnHub } = useCreateReturnHub()
  const { mutateAsync: putReturnHub } = useUpdateReturnHub()
  const { data: returnHub, isFetching } = useGetReturnHub(id || '', {
    query: {
      enabled: !!id,
      select: (item) => convertToReturnHub(item),
    },
  })

  const { addressValidationSchema, updateAddressValidationSchema } = useAddressService(
    returnHub?.address.countryCode ?? 'DE'
  )
  const validationSchema = createReturnHubsValidationSchema(t, addressValidationSchema)

  const onBack = (dirty: boolean) => (dirty ? setCancelDialogOpen(true) : navigate(links.returnHubsLink()))

  const onSubmit = async (newReturnHub: ReturnHub) => {
    const returnHubDto = convertToNewReturnHub(newReturnHub)
    try {
      if (addNew) {
        await postReturnHub({ data: returnHubDto })
      } else {
        await putReturnHub({ data: { ...returnHubDto, id } })
      }
      showSuccess(t('admin.returnHubPage.editReturnHubSucces'))
      navigate(links.returnHubsLink())
    } catch (error) {
      const message = getErrorMessage(error, t('admin.returnHubPage.editReturnHubError'))
      showError(message)
    }
  }

  if (isFetching || (!addNew && !returnHub)) {
    return null
  }

  return (
    <>
      <Formik
        initialValues={addNew ? initialReturnHub : returnHub!}
        validateOnMount
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {({ dirty, values }) => (
          <>
            <Box mb={2}>
              <BackButton onClick={() => onBack(dirty)}>{t('admin.returnHubPage.backButtonTitle')}</BackButton>
            </Box>
            <Card>
              <Card.Header title={t('admin.returnHubPage.titleText')} />
              <Card.Content>
                <ReturnHubForm
                  country={values.address.countryCode || 'DE'}
                  id={id}
                  onCountryChange={updateAddressValidationSchema}
                />
              </Card.Content>
            </Card>
          </>
        )}
      </Formik>
      <CancelDialog open={cancelDialogOpen} onCancel={() => setCancelDialogOpen(false)} />
    </>
  )
}
